/* eslint-disable react/prop-types, react/destructuring-assignment */
import { StatusIcon } from '@augmentt-dev/ui-components'
import clsx from 'clsx'
import React, { useEffect, useRef, useState } from 'react'
import { Button, ButtonGroup, Col, Form, Modal, Row } from 'react-bootstrap'
import BootstrapTable from 'react-bootstrap-table-next'
import filterFactory from 'react-bootstrap-table2-filter'
import paginationFactory, { PaginationListStandalone, PaginationProvider } from 'react-bootstrap-table2-paginator'
import ToolkitProvider from 'react-bootstrap-table2-toolkit'
import { FormattedMessage, injectIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import Select, { components } from 'react-select'
import { getSelectedCustomer } from '../../../../_augmentt/selectors/customers'
import { multiSelectStyle } from '../../../../_augmentt/utils/styles'
import { getCategories } from '../../../crud/app.crud'
import { getCompanyDetails, getDaysActive } from '../../../crud/elastic.crud'
import { OFFICE365 } from '../../../crud/integration.crud'
import useCustomerIntegration from '../../../hooks/useCustomerIntegration'
import { GeneralError } from '../../../partials/content/GeneralError'
import SaaSApplicationLogo from '../../../partials/content/SaaSApplicationLogo'
import ScrollMenu from '../../../partials/content/ScrollMenu'
import Spinner from '../../../partials/content/Spinner'
import { getEmployeeFullName } from '../../../util/table.util'
import CompanyDetailsNotes from '../../../widgets/CompanyDetailsNotes'
import DaysActiveWidget from '../../../widgets/DaysActiveWidget'
import { useIntegrationEmployees } from '../utils/useIntegrationEmployees'
import EditApplicationModal from './EditApplicationModal'
import EditEmployeeModal from './EditEmployeeModal'
import ViewEmployeeDataModal from './ViewEmployeeDataModal'

const mapStatusToScore = (statusValue) => {
  const numericValue = Number(statusValue)
  if (Number.isFinite(numericValue)) {
    return numericValue
  }

  switch (statusValue) {
    case 'Approved':
      return 3
    case 'Pending Review':
      return 2
    case 'Restricted':
      return 1
    default:
      return 0
  }
}

const mapLevelToScore = (levelValue) => {
  const numericValue = Number(levelValue)
  if (Number.isFinite(numericValue)) {
    return numericValue
  }

  switch (levelValue) {
    case 'High':
      return 3
    case 'Medium':
      return 2
    case 'Low':
      return 1
    default:
      return 0
  }
}

function ApplicationDetailsModal(props) {
  const {
    intl,
    show,
    onHide,
    customer,
    application,
    applicationDataFromTable,
    showAppDetails,
    reload,
    usageDateRange,
  } = props

  const currentUser = useSelector(({ user }) => user.data)
  const selectedDateRange = useSelector(({ dates }) => dates.data)
  const dateRange = usageDateRange ?? selectedDateRange
  const allowPermissions = useSelector(({ userPermissions }) => userPermissions?.data?.permissions)
  const selectedCustomer = useSelector(getSelectedCustomer)
  const hasManagePermission = allowPermissions?.discover === 'manage'

  const [selectedEmployee, setSelectedEmployee] = useState()
  const [modalEditEmployeeShow, setModalEditEmployeeShow] = useState(false)
  const [employeePopoutShow, setEmployeePopoutShow] = useState(false)

  // General local state
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [selectedTab, setSelectedTab] = useState('users')

  // User data
  const [userData, setUserData] = useState()
  const [unfilteredEmployeesUsage, setUnfilteredEmployeesUsage] = useState()
  const [filteredEmployeesUsage, setFilteredEmployeesUsage] = useState()

  // Application details data
  const [applicationData, setApplicationData] = useState()
  const [tableFallbackData, setTableFallbackData] = useState()
  const [editedApplicationOverrides, setEditedApplicationOverrides] = useState()
  const [statusColor, setStatusColor] = useState()
  const [riskColor, setRiskColor] = useState()
  const [productivityColor, setProductivityColor] = useState()
  const [categories, setCategories] = useState()
  const [showEditApplicationModal, setShowEditApplicationModal] = useState(false)

  // Table State
  const [numPerPage, setNumPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  const [showDiscoveredMenu, setShowDiscoveredMenu] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)

  const [discoveredOptions, setDiscoveredOptions] = useState([])
  const [userOptions, setUserOptions] = useState([])

  const [discoveredSelectedOptions, setDiscoveredSelectedOptions] = useState([])
  const [userSelectedOptions, setUserSelectedOptions] = useState([])

  const acceptedColor = 'var(--bs-success)'
  const mediumColor = 'var(--bs-warning)'
  const dangerColor = 'var(--bs-danger)'

  const discoveredRef = useRef()
  const userRef = useRef()
  const integrationEmployeesRef = useRef([])

  const customerIntegration = useCustomerIntegration({
    selectedCustomerId: selectedCustomer?.id,
  })
  const integrationAppName = customerIntegration?.IntegrationApplication?.application_name ?? OFFICE365
  const { requestIntegrationEmployees } = useIntegrationEmployees({
    integrationAppName,
    selectedCustomerId: selectedCustomer?.id,
    product: 'any',
  })

  useEffect(() => {
    const getIntegrationEmployeesOnLoad = async () => {
      // Get Integration Employees
      integrationEmployeesRef.current = await requestIntegrationEmployees({ force: true })
      setIsLoading(false)
    }

    setIsLoading(true)
    getIntegrationEmployeesOnLoad()
  }, [selectedCustomer]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTableFallbackData(applicationDataFromTable)
  }, [applicationDataFromTable])

  const getStatusOrProductivityColor = (score) => {
    if (score === 3) return acceptedColor
    if (score === 2) return mediumColor
    if (score === 1) return dangerColor
    return 'var(--bs-tertiary-bg)'
  }

  const getRiskColor = (score) => {
    if (score === 3) return dangerColor
    if (score === 2) return mediumColor
    if (score === 1) return acceptedColor
    return 'var(--bs-tertiary-bg)'
  }

  const setApplicationDetailsState = (appData) => {
    setApplicationData(appData)
    setStatusColor(getStatusOrProductivityColor(appData?.APPROVAL_STATUS))
    setProductivityColor(getStatusOrProductivityColor(appData?.PRODUCTIVITY))
    setRiskColor(getRiskColor(appData?.RISK))
  }

  useEffect(() => {
    if (showAppDetails !== undefined) {
      setSelectedTab(showAppDetails ? 'details' : 'users')
    }
    // Get all data at initial open since we need information from both endpoints
    if (application && show && dateRange) {
      // eslint-disable-next-line no-use-before-define
      getUserData(application, showAppDetails === true)
      // eslint-disable-next-line no-use-before-define
      getApplicationData(application, showAppDetails === false)
    }

    // Adding click handler to control multiselect menu visibility
    // eslint-disable-next-line no-use-before-define
    document.addEventListener('click', handleClickOutside, true)
    return () => {
      // eslint-disable-next-line no-use-before-define
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [customer, application, show]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handles updating the table data after selecting an option in a column header
  useEffect(() => {
    if (filteredEmployeesUsage?.length) {
      // eslint-disable-next-line no-use-before-define
      const filteredData = filterTableData(filteredEmployeesUsage)
      setFilteredEmployeesUsage(filteredData)
    }
  }, [userSelectedOptions, discoveredSelectedOptions]) // eslint-disable-line react-hooks/exhaustive-deps

  // Retrieves the employee usage data from /elastic/usage/
  const getUserData = async (applicationUrl, loading = false) => {
    if (loading) {
      setIsLoading(true)
    }

    try {
      const elasticUsageResponse = (
        await getDaysActive(
          customer?.id ? customer.id : '*',
          [applicationUrl],
          dateRange?.startDate,
          dateRange?.endDate,
          true,
        )
      ).data
      const elasticUsageData = elasticUsageResponse?.activeUsers?.length
        ? elasticUsageResponse.activeUsers[0]
        : undefined
      setUserData(elasticUsageData)
      setUnfilteredEmployeesUsage(
        elasticUsageData && elasticUsageData.employees?.length ? elasticUsageData.employees : undefined,
      )
      setFilteredEmployeesUsage(
        elasticUsageData && elasticUsageData.employees?.length ? elasticUsageData.employees : undefined,
      )
      // eslint-disable-next-line no-use-before-define
      formatTableData(elasticUsageData?.employees ? elasticUsageData.employees : [])
      setCurrentPage(1)
      setNumPerPage(10)
      if (loading) {
        setIsLoading(false)
      }
    } catch (e) {
      console.error(e)
      setIsError(true)
      if (loading) {
        setIsLoading(false)
      }
    }
  }

  // Retrieves the application details data from /elastic/info/
  // eslint-disable-next-line no-shadow
  const getApplicationData = async (application, loading = false, localOverrides = editedApplicationOverrides) => {
    if (loading) {
      setIsLoading(true)
    }

    try {
      const applicationDetailsResponse = (await getCompanyDetails(application, customer)).data

      const tableSourceData = tableFallbackData || applicationDataFromTable

      const fallbackApplicationData = tableSourceData
        ? {
            COMPANY_NAME: tableSourceData?.key,
            CATEGORY: tableSourceData?.CATEGORY,
            APPROVAL_STATUS: mapStatusToScore(tableSourceData?.APPROVAL_STATUS),
            PRODUCTIVITY: mapLevelToScore(tableSourceData?.PRODUCTIVITY),
            RISK: mapLevelToScore(tableSourceData?.RISK),
            URL: tableSourceData?.URL,
          }
        : {}

      const isAllCompaniesView = !customer?.id || customer?.id === '*'

      const resolveScore = (apiValue, tableValue, preferTable = false) => {
        const apiScore = Number(apiValue)
        const tableScore = Number(tableValue)

        if (preferTable) {
          if (Number.isFinite(tableScore)) {
            return tableScore
          }
          if (Number.isFinite(apiScore)) {
            return apiScore
          }
        }

        // For tenant/company view, API value is authoritative even when it is 0 (Unknown).
        if (Number.isFinite(apiScore)) {
          return apiScore
        }
        if (Number.isFinite(tableScore)) {
          return tableScore
        }
        return apiValue ?? tableValue
      }

      const resolveTextField = (apiValue, tableValue, preferTable = false) => {
        if (preferTable && tableValue) {
          return tableValue
        }
        return apiValue ?? tableValue
      }

      const mergedApplicationData = {
        ...applicationDetailsResponse,
        COMPANY_NAME: resolveTextField(
          applicationDetailsResponse?.COMPANY_NAME,
          fallbackApplicationData?.COMPANY_NAME,
          isAllCompaniesView,
        ),
        CATEGORY: resolveTextField(
          applicationDetailsResponse?.CATEGORY,
          fallbackApplicationData?.CATEGORY,
          isAllCompaniesView,
        ),
        APPROVAL_STATUS: resolveScore(
          applicationDetailsResponse?.APPROVAL_STATUS,
          fallbackApplicationData?.APPROVAL_STATUS,
          isAllCompaniesView,
        ),
        PRODUCTIVITY: resolveScore(
          applicationDetailsResponse?.PRODUCTIVITY,
          fallbackApplicationData?.PRODUCTIVITY,
          isAllCompaniesView,
        ),
        RISK: resolveScore(applicationDetailsResponse?.RISK, fallbackApplicationData?.RISK, isAllCompaniesView),
        URL: resolveTextField(applicationDetailsResponse?.URL, fallbackApplicationData?.URL, isAllCompaniesView),
      }
      const mergedWithOverrides = {
        ...mergedApplicationData,
        COMPANY_NAME: localOverrides?.COMPANY_NAME ?? mergedApplicationData.COMPANY_NAME,
        CATEGORY: localOverrides?.CATEGORY ?? mergedApplicationData.CATEGORY,
        APPROVAL_STATUS: localOverrides?.APPROVAL_STATUS ?? mergedApplicationData.APPROVAL_STATUS,
        PRODUCTIVITY: localOverrides?.PRODUCTIVITY ?? mergedApplicationData.PRODUCTIVITY,
        RISK: localOverrides?.RISK ?? mergedApplicationData.RISK,
        URL: localOverrides?.URL ?? mergedApplicationData.URL,
      }
      setCurrentPage(1)
      setNumPerPage(10)
      setApplicationDetailsState(mergedWithOverrides)
      if (loading) {
        setIsLoading(false)
      }
    } catch (e) {
      console.error(e)
      setIsError(true)
      if (loading) {
        setIsLoading(false)
      }
    }
  }

  const onEditApplicationClick = async () => {
    setIsLoading(true)
    setCategories(await getCategories(customer?.id))
    setShowEditApplicationModal(true)
    setIsLoading(false)
  }

  // Formats the options for the column filters as well as the counts of each option
  const formatTableData = async (employeeArray) => {
    let employeeData = []
    let discoveredData = []

    for (let i = 0; i < employeeArray?.length; i += 1) {
      const employeeObject = employeeArray[i]

      // Employee
      if (employeeObject?.employee) {
        employeeData.push({
          value: employeeObject?.employee,
          label: employeeObject?.employee,
        })
      }

      // Discovered
      // eslint-disable-next-line no-restricted-syntax, no-unsafe-optional-chaining
      for (const discoveredString of employeeObject?.dataType) {
        if (discoveredString === 'browser_history') {
          discoveredData.push({ value: discoveredString, label: 'Browser' })
        } else {
          discoveredData.push({ value: discoveredString, label: 'Desktop' })
        }
      }
    }

    const discoveredMap = new Map()
    // eslint-disable-next-line no-restricted-syntax
    for (const discoveredObject of discoveredData) {
      const currentDiscoveredCount = discoveredMap.get(discoveredObject.label)

      if (currentDiscoveredCount !== undefined) {
        discoveredMap.set(discoveredObject.label, currentDiscoveredCount + 1)
      } else {
        discoveredMap.set(discoveredObject.label, 1)
      }
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const discoveredObject of discoveredData) {
      discoveredObject.count = discoveredMap.get(discoveredObject.label)
    }

    employeeData = employeeData.filter((v, i, a) => a.findIndex((t) => t.label === v.label) === i)
    employeeData.sort((a, b) => a.label.toString().localeCompare(b.label))
    setUserOptions(employeeData)

    discoveredData = discoveredData.filter((v, i, a) => a.findIndex((t) => t.label === v.label) === i)
    discoveredData.sort((a, b) => a.label.toString().localeCompare(b.label))
    setDiscoveredOptions(discoveredData)
  }

  const filterTableData = (tableDataToFilter) => {
    const filterHelper = (field, filterOptions) => {
      if (filterOptions?.length) {
        // eslint-disable-next-line no-param-reassign
        tableDataToFilter = tableDataToFilter.filter((tableField) =>
          filterOptions.some((option) => option.label === tableField[`${field}`]),
        )
      }
    }

    const filterHelperArray = (field, filterOptions) => {
      if (filterOptions?.length) {
        // eslint-disable-next-line no-param-reassign
        tableDataToFilter = tableDataToFilter.filter((tableField) =>
          filterOptions.some((filterOption) =>
            tableField[field].some((fieldString) => fieldString === filterOption.value),
          ),
        )
      }
    }

    filterHelper('employee', userSelectedOptions)
    filterHelperArray('dataType', discoveredSelectedOptions)

    return tableDataToFilter
  }

  // Handles change of all filters
  const updateFilter = (filterType, selectedFilterValueArray) => {
    // Back to page 1.
    setCurrentPage(1)

    // Reset employees
    setFilteredEmployeesUsage(unfilteredEmployeesUsage)

    switch (filterType) {
      case 'user':
        if (selectedFilterValueArray?.length) {
          setUserSelectedOptions(selectedFilterValueArray)
        } else {
          setUserSelectedOptions([])
        }
        break
      case 'discovered':
        if (selectedFilterValueArray?.length) {
          setDiscoveredSelectedOptions(selectedFilterValueArray)
        } else {
          setDiscoveredSelectedOptions([])
        }
        break

      default:
        break
    }
  }

  // Handles closing of modal
  const hide = () => {
    setApplicationData()
    setTableFallbackData()
    setEditedApplicationOverrides()
    setFilteredEmployeesUsage()
    setUnfilteredEmployeesUsage()
    setSelectedTab('users')
    setUserData()
    setShowEditApplicationModal(false)
    onHide()
  }

  // Clear button component override
  // eslint-disable-next-line react/no-unstable-nested-components, no-shadow
  function ClearIndicator({ innerRef, children, ...props }) {
    const onClear = (e) => {
      if (props.selectProps?.name === 'user') {
        updateFilter('user', undefined)
      }
      if (props.selectProps?.name === 'discovered') {
        updateFilter('discovered', undefined)
      }

      e.stopPropagation()
      e.preventDefault()
    }

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div ref={innerRef} className="multi-select-clear" onClick={onClear}>
        <components.ClearIndicator {...props}>
          <span className="icon-xs flaticon2-cross p-2" />
        </components.ClearIndicator>
      </div>
    )
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    document.getElementById('userDetailsTable').scrollIntoView()
  }

  const handleSizePerPageChange = (sizePerPage) => {
    setNumPerPage(sizePerPage)
    setCurrentPage(1)
    document.getElementById('userDetailsTable').scrollIntoView()
  }

  // React-Select value container component override
  // eslint-disable-next-line react/no-unstable-nested-components, no-shadow
  function ValueContainer({ children, getValue, ...props }) {
    const { length } = getValue()
    return (
      <components.ValueContainer {...props}>
        {length === 1 && (
          <>
            <span className="multi-select-value">{props.selectProps.value[0].label}</span>
            <ClearIndicator {...props} />
          </>
        )}
        {length > 1 && (
          <>
            <span className="multi-select-value">{length}</span>
            <ClearIndicator {...props} />
            <components.Placeholder {...props} className="multi-select-label">
              {props.selectProps.placeholder}
            </components.Placeholder>
          </>
        )}
      </components.ValueContainer>
    )
  }

  // Option component override
  // eslint-disable-next-line react/no-unstable-nested-components, no-shadow
  function Option({ children, ...props }) {
    const { isSelected, label, value, isMulti, data } = props

    const onClick = (e) => {
      props.selectOption({ ...data })
      e.stopPropagation()
      e.preventDefault()
    }

    if (isMulti) {
      return (
        <components.Option {...props}>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div className="multi-select-option" onClick={onClick}>
            <Form.Check className="multi-select-check" checked={isSelected} label={label} value={value} />
            {
              // Show count in option if not showing user
              props.selectProps.name !== 'user' && <span className="multi-select-count">{props.data.count}</span>
            }
          </div>
        </components.Option>
      )
    }
  }

  const handleClick = (target) => {
    if (target === 'user') {
      if (showUserMenu) {
        setShowUserMenu(false)
        if (userRef.current.select.inputRef) {
          userRef.current.select.inputRef.blur()
        }
      } else {
        setShowUserMenu(true)
      }
    }
    if (target === 'discovered') {
      if (showDiscoveredMenu) {
        setShowDiscoveredMenu(false)
        if (discoveredRef.current.select.inputRef) {
          discoveredRef.current.select.inputRef.blur()
        }
      } else {
        setShowDiscoveredMenu(true)
      }
    }
  }

  const handleClickOutside = (event) => {
    if (
      userRef.current &&
      !userRef.current.select.controlRef.contains(event.target) &&
      !userRef.current.select?.focusedOptionRef?.contains(event.target)
    ) {
      setShowUserMenu(false)
      if (userRef.current.select.inputRef) {
        userRef.current.select.inputRef.blur()
      }
    }
    if (
      discoveredRef.current &&
      !discoveredRef.current.select.controlRef.contains(event.target) &&
      !discoveredRef.current.select?.focusedOptionRef?.contains(event.target)
    ) {
      setShowDiscoveredMenu(false)
      if (discoveredRef.current.select.inputRef) {
        discoveredRef.current.select.inputRef.blur()
      }
    }
  }

  const formatDiscoveredColumn = (_cell, row) => (
    // Lit when the app was discovered via that source (browser history / desktop apps-in-use).
    <div className="d-flex align-items-center flex-nowrap gap-1">
      <StatusIcon
        icon="fas fa-globe"
        label={intl.formatMessage({ id: 'HOVER.BROWSER' })}
        active={!!row?.dataType?.includes('browser_history')}
      />
      <StatusIcon
        icon="fas fa-desktop"
        label={intl.formatMessage({ id: 'HOVER.DESKTOP' })}
        active={!!row?.dataType?.includes('apps_in_use')}
      />
    </div>
  )
  const formatDaysActiveColumn = (_cell, row) => (
    <div className="userActivityChart ps-3">
      <DaysActiveWidget data={row} />
    </div>
  )

  const formatUserHeader = () => (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="select-container" onClick={(e) => handleClick('user', e)}>
      <Select
        id="user"
        ref={userRef}
        autoBlur
        name="user"
        classNamePrefix="multi-select"
        styles={multiSelectStyle}
        isMulti
        closeMenuOnSelect
        hideSelectedOptions={false}
        menuIsOpen={showUserMenu}
        options={userOptions}
        value={userSelectedOptions}
        onChange={(value) => updateFilter('user', value)}
        placeholder="Users"
        isClearable={false}
        isSearchable
        components={
          userSelectedOptions?.length
            ? { MenuList: ScrollMenu, ClearIndicator, Option, ValueContainer }
            : { MenuList: ScrollMenu, Option }
        }
      />
    </div>
  )

  const formatDiscoveredHeader = () => (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="select-container" onClick={(e) => handleClick('discovered', e)}>
      <Select
        id="discovered"
        ref={discoveredRef}
        autoBlur
        name="discovered"
        classNamePrefix="multi-select"
        styles={multiSelectStyle}
        isMulti
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        menuIsOpen={showDiscoveredMenu}
        options={discoveredOptions}
        value={discoveredSelectedOptions}
        onChange={(value) => updateFilter('discovered', value)}
        placeholder="Discovered"
        isClearable={false}
        isSearchable
        components={
          discoveredSelectedOptions?.length
            ? { MenuList: ScrollMenu, ClearIndicator, Option, ValueContainer }
            : { MenuList: ScrollMenu, Option }
        }
      />
    </div>
  )

  const onEditEmployeeClick = (obj) => {
    setModalEditEmployeeShow(true)
    setSelectedEmployee(obj)
  }

  const onEmployeePopoutShow = async (obj) => {
    const employeeData = integrationEmployeesRef.current.filter((item) => item?.employeeIdentifier === obj?.id)
    const employeeId = employeeData[0]?.employeeId

    if (employeeId) {
      setSelectedEmployee({ employeeId, ...obj })
      setEmployeePopoutShow(true)
    } else {
      // eslint-disable-next-line no-console
      console.log(`Error finding user ${getEmployeeFullName(obj)}, id ${obj.id}`)
    }
  }

  const columns = [
    {
      dataField: 'user',
      text: 'Users',
      // eslint-disable-next-line react/no-unstable-nested-components
      formatter: (_cell, row) => (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <span className="cursor_pointer" onClick={() => onEmployeePopoutShow(row)}>
          {row.employee}
        </span>
      ),
      headerFormatter: formatUserHeader,
      title: true,
      headerStyle: () => ({ width: '220px' }),
    },
    {
      dataField: 'discovered',
      text: 'Discovered',
      formatter: formatDiscoveredColumn,
      headerFormatter: formatDiscoveredHeader,
      title: false,
      headerStyle: () => ({ width: '200px' }),
    },
    {
      dataField: 'daysActive',
      text: 'Days Active',
      formatter: formatDaysActiveColumn,
    },
  ]

  const hasUpdated = (updatedApplication) => {
    let optimisticOverrides

    if (updatedApplication) {
      optimisticOverrides = {
        COMPANY_NAME: updatedApplication?.companyName,
        CATEGORY: updatedApplication?.category,
        APPROVAL_STATUS: Number.isFinite(Number(updatedApplication?.approvalStatus))
          ? Number(updatedApplication?.approvalStatus)
          : undefined,
        RISK: Number.isFinite(Number(updatedApplication?.risk)) ? Number(updatedApplication?.risk) : undefined,
        PRODUCTIVITY: Number.isFinite(Number(updatedApplication?.productivity))
          ? Number(updatedApplication?.productivity)
          : undefined,
        URL: updatedApplication?.url,
      }

      setTableFallbackData((prev) => ({
        ...prev,
        key: updatedApplication?.companyName ?? prev?.key,
        CATEGORY: updatedApplication?.category ?? prev?.CATEGORY,
        APPROVAL_STATUS: updatedApplication?.approvalStatus ?? prev?.APPROVAL_STATUS,
        RISK: updatedApplication?.risk ?? prev?.RISK,
        PRODUCTIVITY: updatedApplication?.productivity ?? prev?.PRODUCTIVITY,
        URL: updatedApplication?.url ?? prev?.URL,
      }))
      setEditedApplicationOverrides(optimisticOverrides)

      const optimisticApplicationData = {
        // Preserve previous values when payload fields are missing.
        ...(applicationData || {}),
        COMPANY_NAME: updatedApplication?.companyName ?? applicationData?.COMPANY_NAME,
        CATEGORY: updatedApplication?.category ?? applicationData?.CATEGORY,
        APPROVAL_STATUS: Number.isFinite(Number(updatedApplication?.approvalStatus))
          ? Number(updatedApplication?.approvalStatus)
          : applicationData?.APPROVAL_STATUS,
        RISK: Number.isFinite(Number(updatedApplication?.risk))
          ? Number(updatedApplication?.risk)
          : applicationData?.RISK,
        PRODUCTIVITY: Number.isFinite(Number(updatedApplication?.productivity))
          ? Number(updatedApplication?.productivity)
          : applicationData?.PRODUCTIVITY,
        URL: updatedApplication?.url ?? applicationData?.URL,
      }
      setApplicationDetailsState(optimisticApplicationData)
    }

    // Grab updated application data
    if (application && show && dateRange) {
      getApplicationData(application, false, updatedApplication ? optimisticOverrides : editedApplicationOverrides)
    }
  }

  // Hide modals
  const modalClose = () => {
    setModalEditEmployeeShow(false)
    setEmployeePopoutShow(false)
  }

  return (
    <>
      <Form>
        <Modal
          size="lg"
          aria-labelledby="contained-modal-title-vcenter"
          centered
          scrollable
          show={show}
          onHide={hide}
          id="applicationDetailsModal"
        >
          {/* Header */}
          <div className="app-details-header">
            <Row>
              <Col>
                <button
                  type="button"
                  className="close-button"
                  onClick={() => hide()}
                  aria-label={intl.formatMessage({ id: 'GENERAL.CLOSE' })}
                >
                  <i className="fas fa-xmark" />
                </button>
              </Col>
            </Row>
            <Row>
              <Col sm="auto" md="auto" lg="auto">
                <div className="app-details-image">
                  <SaaSApplicationLogo domain={applicationData?.URL} />
                </div>
              </Col>
              <Col sm="auto" md="auto" lg="auto">
                <Row>
                  <Col>
                    <span className="app-details-name">
                      {applicationData?.COMPANY_NAME ? applicationData.COMPANY_NAME : 'Application Details'}
                    </span>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <p
                      style={{
                        color: 'var(--bs-secondary-color)',
                        marginBottom: '0px',
                        marginTop: '8px',
                        lineHeight: '9px',
                      }}
                    >
                      Last used: {userData?.lastVisited ? userData.lastVisited : 'Not available'}
                    </p>
                  </Col>
                </Row>
              </Col>
            </Row>

            <div className="app-details-button-row">
              <Row>
                <Col>
                  <div style={{ display: 'flex' }}>
                    <button
                      type="button"
                      onClick={() => setSelectedTab('users')}
                      className={`font-bolder ${clsx({
                        companyDetailsTabButtonSelected: selectedTab === 'users',
                        companyDetailsTabButtonDeselected: selectedTab === 'details',
                      })}`}
                    >
                      <FormattedMessage id="ADMINISTRATION.USERS" />
                    </button>

                    <button
                      type="button"
                      onClick={() => setSelectedTab('details')}
                      className={`font-bolder ${clsx({
                        companyDetailsTabButtonSelected: selectedTab === 'details',
                        companyDetailsTabButtonDeselected: selectedTab === 'users',
                      })}`}
                      style={{ marginLeft: '10px' }}
                    >
                      <FormattedMessage id="ADMINISTRATION.DETAILS" />
                    </button>
                  </div>
                </Col>
              </Row>
            </div>
          </div>

          <Modal.Body>
            <div>
              {/* eslint-disable no-nested-ternary */}
              {isError ? (
                <Row>
                  <Col style={{ display: 'flex' }}>
                    <GeneralError />
                  </Col>
                </Row>
              ) : isLoading ? (
                <Row>
                  <Col style={{ display: 'flex' }}>
                    <Spinner />
                  </Col>
                </Row>
              ) : (
                <>
                  {/* Users Tab */}
                  {selectedTab === 'users' && (
                    <>
                      {/* Users Count */}
                      <Row>
                        <Col>
                          <p className="font-bolder ps-4" style={{ fontSize: '14px' }}>
                            <FormattedMessage
                              id="GENERAL.USERS"
                              values={{
                                users: userData?.totalUsers !== undefined ? userData.totalUsers : 0,
                              }}
                            />
                          </p>
                        </Col>
                      </Row>

                      {/* Users Table */}
                      <Row>
                        <Col>
                          <div id="userDetailsTable">
                            <PaginationProvider
                              pagination={paginationFactory({
                                custom: true,
                                page: currentPage,
                                totalSize: filteredEmployeesUsage?.length,
                                sizePerPage: numPerPage,
                                onPageChange: handlePageChange,
                                onSizePerPageChange: handleSizePerPageChange,
                                sizePerPageList: [
                                  {
                                    text: '10',
                                    value: 10,
                                  },
                                  {
                                    text: '25',
                                    value: 25,
                                  },
                                  {
                                    text: '50',
                                    value: 50,
                                  },
                                  {
                                    text: '100',
                                    value: 100,
                                  },
                                ],
                              })}
                            >
                              {({ paginationProps, paginationTableProps }) => (
                                <ToolkitProvider
                                  keyField="user"
                                  data={filteredEmployeesUsage?.length ? filteredEmployeesUsage : []}
                                  columns={columns}
                                >
                                  {/* eslint-disable-next-line no-shadow */}
                                  {(props) => (
                                    <Row>
                                      <Col md="12">
                                        <BootstrapTable
                                          {...props.baseProps}
                                          wrapperClasses="table-legacy"
                                          keyField="user"
                                          loading={isLoading}
                                          filter={filterFactory()}
                                          bordered={false}
                                          hover={!!(!isLoading && filteredEmployeesUsage?.length)}
                                          bootstrap4
                                          sort={{
                                            dataField: 'user',
                                            order: 'asc',
                                          }}
                                          {...paginationTableProps}
                                        />
                                        {filteredEmployeesUsage?.length > 10 && (
                                          <div className="pagination">
                                            <div>
                                              <ButtonGroup>
                                                {paginationProps.sizePerPageList.map((option) => (
                                                  <Button
                                                    key={option.text}
                                                    onClick={() => handleSizePerPageChange(option.value)}
                                                    variant={numPerPage === option.value ? 'primary' : 'light'}
                                                  >
                                                    {option.text}
                                                  </Button>
                                                ))}
                                              </ButtonGroup>
                                            </div>
                                            <div>
                                              <PaginationListStandalone {...paginationProps} />
                                            </div>
                                          </div>
                                        )}
                                      </Col>
                                    </Row>
                                  )}
                                </ToolkitProvider>
                              )}
                            </PaginationProvider>
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}

                  {/* Details Tab */}
                  {!isLoading && selectedTab === 'details' && (
                    <div className="app-details">
                      <Row>
                        <Col>
                          <p className="font-bolder" style={{ marginBottom: '0px' }}>
                            <FormattedMessage id="GENERAL.APPLICATION_DETAILS" />
                          </p>
                        </Col>

                        <Col className="table-actions">
                          {hasManagePermission && (
                            <Button
                              variant="link"
                              onClick={() => {
                                onEditApplicationClick()
                              }}
                            >
                              <FormattedMessage id="GENERAL.EDIT" />
                            </Button>
                          )}
                        </Col>
                      </Row>

                      {/* Category */}
                      <Row>
                        <Col className="detail-col">
                          <p className="font-bolder font-color-6">
                            <FormattedMessage id="GENERAL.CATEGORY" />
                            {': '}
                          </p>
                        </Col>

                        <Col
                          className="flex-vertical-align"
                          style={{
                            marginTop: '4px',
                            color: 'var(--bs-body-color)',
                            fontSize: '13px',
                          }}
                        >
                          {applicationData?.CATEGORY ? (
                            applicationData.CATEGORY
                          ) : (
                            <FormattedMessage id="GENERAL.NOT_AVAILABLE" />
                          )}
                        </Col>
                      </Row>

                      {/* Approval Status */}
                      <Row>
                        <Col className="detail-col">
                          <p className="font-bolder font-color-6">
                            <FormattedMessage id="GENERAL.STATUS" />
                            {': '}
                          </p>
                        </Col>

                        <Col
                          className="widget4 flex-vertical-align"
                          style={{
                            width: 'fit-content',
                            minHeight: '0px',
                            marginTop: '4px',
                          }}
                        >
                          <div className="legend">
                            <div className="bullet" style={{ backgroundColor: statusColor }} />
                            <span style={{ color: 'var(--bs-body-color)', fontSize: '13px' }}>
                              <FormattedMessage
                                id={
                                  applicationData?.APPROVAL_STATUS === 3
                                    ? 'SAASCONFIG.APPROVED'
                                    : applicationData?.APPROVAL_STATUS === 2
                                      ? 'SAASCONFIG.PENDING_REVIEW'
                                      : applicationData?.APPROVAL_STATUS === 1
                                        ? 'SAASCONFIG.RESTRICTED'
                                        : 'SAASCONFIG.UNKNOWN'
                                }
                              />
                            </span>
                          </div>
                        </Col>
                      </Row>

                      {/* Risk Level */}
                      <Row>
                        <Col className="detail-col">
                          <p className="font-bolder font-color-6">
                            <FormattedMessage id="DISCOVER.RISK_LEVEL" />
                            {': '}
                          </p>
                        </Col>

                        <Col
                          className="widget4 flex-vertical-align"
                          style={{
                            width: 'fit-content',
                            minHeight: '0px',
                            marginTop: '4px',
                          }}
                        >
                          <div className="legend">
                            <div className="bullet" style={{ backgroundColor: riskColor }} />
                            <span style={{ color: 'var(--bs-body-color)', fontSize: '13px' }}>
                              <FormattedMessage
                                id={
                                  applicationData?.RISK === 3
                                    ? 'SAASCONFIG.HIGH'
                                    : // eslint-disable-next-line no-nested-ternary
                                      applicationData?.RISK === 2
                                      ? 'SAASCONFIG.MEDIUM'
                                      : applicationData?.RISK === 1
                                        ? 'SAASCONFIG.LOW'
                                        : 'SAASCONFIG.UNKNOWN'
                                }
                              />
                            </span>
                          </div>
                        </Col>
                      </Row>

                      {/* Productivity */}
                      <Row>
                        <Col className="detail-col">
                          <p className="font-bolder font-color-6">
                            <FormattedMessage id="DISCOVER.PRODUCTIVITY" />
                            {': '}
                          </p>
                        </Col>

                        <Col
                          className="widget4 flex-vertical-align"
                          style={{
                            width: 'fit-content',
                            minHeight: '0px',
                            marginTop: '4px',
                          }}
                        >
                          <div className="legend">
                            <div className="bullet" style={{ backgroundColor: productivityColor }} />
                            <span style={{ color: 'var(--bs-body-color)', fontSize: '13px' }}>
                              <FormattedMessage
                                id={
                                  // eslint-disable-next-line no-nested-ternary
                                  applicationData?.PRODUCTIVITY === 3
                                    ? 'SAASCONFIG.HIGH'
                                    : // eslint-disable-next-line no-nested-ternary
                                      applicationData?.PRODUCTIVITY === 2
                                      ? 'SAASCONFIG.MEDIUM'
                                      : applicationData?.PRODUCTIVITY === 1
                                        ? 'SAASCONFIG.LOW'
                                        : 'SAASCONFIG.UNKNOWN'
                                }
                              />
                            </span>
                          </div>
                        </Col>
                      </Row>

                      <hr />

                      {/* Company Information */}
                      <Row>
                        <Col>
                          <p className="font-bolder" style={{ marginBottom: '0px' }}>
                            <FormattedMessage id="GENERAL.COMPANY_INFORMATION" />
                          </p>
                        </Col>
                      </Row>

                      <Row style={{ marginTop: '10px' }}>
                        <Col>
                          {applicationData?.FIRST_NAME && (
                            <p className="info">
                              {applicationData?.FIRST_NAME} {applicationData?.LAST_NAME}
                              {applicationData?.TITLE && ` - ${applicationData?.TITLE}`}
                            </p>
                          )}

                          {/* Founder's Email */}
                          {applicationData?.EMAIL && (
                            <Row>
                              <Col>
                                <p className="info">
                                  <a href={`mailto:${applicationData.EMAIL}`}>{applicationData.EMAIL}</a>
                                </p>
                              </Col>
                            </Row>
                          )}

                          {/* Year Founded */}
                          {applicationData?.YEAR_FOUNDED && (
                            <Row style={{ marginTop: '15px' }}>
                              <Col>
                                <p className="info">
                                  <FormattedMessage id="FORM.FOUNDED" />: {applicationData.YEAR_FOUNDED}
                                </p>
                              </Col>
                            </Row>
                          )}

                          {/* Phone */}
                          {applicationData?.PHONE_NUMBER && (
                            <Row style={{ marginTop: '15px' }}>
                              <Col>
                                <p className="info">
                                  <FormattedMessage id="FORM.PHONE_2" />: {applicationData.PHONE_NUMBER}
                                </p>
                              </Col>
                            </Row>
                          )}

                          {/* Website Link */}
                          {applicationData?.WEBSITE && (
                            <>
                              {!applicationData?.PHONE_NUMBER && <div style={{ marginTop: '15px' }} />}

                              <Row>
                                <Col>
                                  <p className="info">
                                    <FormattedMessage id="FORM.WEBSITE" />
                                    <span>{': '}</span>
                                    <span>
                                      <a href={applicationData?.WEBSITE} target="_blank" rel="noopener noreferrer">
                                        {applicationData.WEBSITE}
                                      </a>
                                    </span>
                                  </p>
                                </Col>
                              </Row>
                            </>
                          )}

                          {/* No Company Details Available */}
                          {!applicationData?.WEBSITE &&
                            !applicationData?.PHONE_NUMBER &&
                            !applicationData?.YEAR_FOUNDED &&
                            !applicationData?.EMAIL &&
                            !applicationData?.FIRST_NAME && (
                              <Row>
                                <Col>
                                  <p className="info">
                                    <FormattedMessage id="GENERAL.NO_DETAILS_FOUND" />
                                  </p>
                                </Col>
                              </Row>
                            )}
                        </Col>
                      </Row>

                      <hr />

                      {/* Links */}
                      <Row>
                        <Col>
                          <p className="font-bolder" style={{ marginBottom: '0px' }}>
                            <FormattedMessage id="GENERAL.LINKS" />
                          </p>
                        </Col>
                      </Row>

                      <Row style={{ marginTop: '10px' }}>
                        <Col>
                          {/* Linkedin */}
                          {applicationData?.COMPANY_LINKEDIN && (
                            <p className="info">
                              <a href={applicationData?.COMPANY_LINKEDIN} target="_blank" rel="noopener noreferrer">
                                <FormattedMessage id="FORM.LINKEDIN" />
                              </a>
                            </p>
                          )}

                          {/* Pricing */}
                          <Row>
                            <Col>
                              {applicationData?.PRICING_URL &&
                                applicationData?.PRICING_URL !== '#VALUE!' &&
                                applicationData?.PRICING_URL !== '<none>' && (
                                  <p className="info">
                                    <a href={applicationData?.PRICING_URL} target="_blank" rel="noopener noreferrer">
                                      <FormattedMessage id="GENERAL.PRICING" />
                                    </a>
                                  </p>
                                )}
                            </Col>
                          </Row>

                          {/* Privacy Policy */}
                          <Row>
                            <Col>
                              {applicationData?.PRIVACY_URL &&
                                applicationData?.PRIVACY_URL !== '#VALUE!' &&
                                applicationData?.PRIVACY_URL !== '<none>' && (
                                  <p className="info">
                                    <a href={applicationData?.PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                                      <FormattedMessage id="GENERAL.PRIVACY_POLICY" />
                                    </a>
                                  </p>
                                )}
                            </Col>
                          </Row>

                          {/* Legal */}
                          <Row>
                            <Col>
                              {applicationData?.LEGAL_URL &&
                                applicationData?.LEGAL_URL !== '#VALUE!' &&
                                applicationData?.LEGAL_URL !== '<none>' && (
                                  <p className="info">
                                    <a href={applicationData?.LEGAL_URL} target="_blank" rel="noopener noreferrer">
                                      <FormattedMessage id="GENERAL.LEGAL" />
                                    </a>
                                  </p>
                                )}
                            </Col>
                          </Row>

                          {/* Terms of Service */}
                          <Row>
                            <Col>
                              {applicationData?.TERMS_URL &&
                                applicationData?.TERMS_URL !== '#VALUE!' &&
                                applicationData?.TERMS_URL !== '<none>' && (
                                  <p className="info">
                                    <a href={applicationData?.TERMS_URL} target="_blank" rel="noopener noreferrer">
                                      <FormattedMessage id="GENERAL.TERMS_OF_SERVICE" />
                                    </a>
                                  </p>
                                )}
                            </Col>
                          </Row>

                          {/* No Links Available */}
                          {!applicationData?.COMPANY_LINKEDIN &&
                            !applicationData?.PRICING_URL &&
                            !applicationData?.PRIVACY_URL &&
                            !applicationData?.LEGAL_URL &&
                            !applicationData?.TERMS_URL && (
                              <Row>
                                <Col>
                                  <p className="info">
                                    <FormattedMessage id="GENERAL.NO_DETAILS_FOUND" />
                                  </p>
                                </Col>
                              </Row>
                            )}
                        </Col>
                      </Row>

                      {/* Notes */}
                      <Row>
                        <Col>
                          <CompanyDetailsNotes
                            notes={applicationData?.notes || []}
                            customer={customer}
                            application={application}
                            showNotes={selectedTab === 'details'}
                          />
                        </Col>
                      </Row>

                      {showEditApplicationModal && (
                        <EditApplicationModal
                          show={showEditApplicationModal}
                          onUpdate={hasUpdated}
                          onHide={() => {
                            setShowEditApplicationModal(false)
                          }}
                          customer={customer}
                          orgId={
                            customer?.Organization?.id
                              ? customer.Organization.id
                              : currentUser?.Customer?.Organization?.id
                          }
                          application={applicationData}
                          categories={categories}
                        />
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            {/* eslint-enable no-nested-ternary */}
          </Modal.Body>

          <Modal.Footer>
            <Button onClick={hide} variant="light">
              <FormattedMessage id="GENERAL.CLOSE" />
            </Button>
          </Modal.Footer>
        </Modal>
      </Form>
      <EditEmployeeModal
        show={modalEditEmployeeShow}
        onHide={modalClose}
        onUpdate={() => reload()}
        employee={selectedEmployee}
      />
      {selectedEmployee && (
        <ViewEmployeeDataModal
          show={employeePopoutShow}
          onHide={modalClose}
          onEdit={() => onEditEmployeeClick(selectedEmployee)}
          tableRowEmployee={selectedEmployee}
          selectedCustomer={selectedCustomer}
        />
      )}
    </>
  )
}

export default injectIntl(ApplicationDetailsModal)
